// libraries
import { useCallback, useEffect, useState } from 'react';
import { BackTop, notification } from 'antd';
import styled from 'styled-components';

// components
import { ReportListHeader } from './report-list-header';
import { ReportListContentDesktop } from './report-list-content.desktop';
import { ReportListContentMobile } from './report-list-content.mobile';

// services
import * as ReportService from '../../../services/bb-api/report';

// hooks
import { useDeviceSize } from '../../../hooks/device-size';

// utils
import { txt } from '../../../locales/i18n';

function contentTypeMapper(type: string): string {
	switch (type) {
		case 'post': {
			return txt('global.post');
		}

		case 'conversation': {
			return txt('global.conversation');
		}

		case 'activity': {
			return txt('global.activity');
		}

		case 'union': {
			return txt('global.union');
		}

		default: {
			return txt('global.unknown');
		}
	}
}

export function ReportList(): JSX.Element {
	const [loading, setLoading] = useState<boolean>(true);
	const [dataSource, setDataSource] = useState<any[]>([]);
	const [totalMatches, setTotalMatches] = useState<number>(0);
	const isMobileDevice: boolean = useDeviceSize();

	const getReports = useCallback(async (): Promise<void> => {
		setLoading(true);

		try {
			const reports: Record<string, any>[] = await ReportService.query();
			reports.forEach((report: Record<string, any>) => {
				report.contentType = contentTypeMapper(report.contentType);
			});
			setDataSource(reports);

			const [entryReport]: Record<string, any>[] = reports;
			setTotalMatches(entryReport?.totalMatches ?? 0);
		} catch (error) {
			setDataSource([]);
			setTotalMatches(0);

			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}, [setDataSource, setLoading]);

	useEffect(() => {
		getReports();
	}, [getReports]);

	return (
		<Container>
			<ReportListHeader totalMatches={totalMatches} loading={loading} />

			{isMobileDevice === false && (
				<ReportListContentDesktop
					getReports={getReports}
					dataSource={dataSource}
					useLoading={[loading, setLoading]}
				/>
			)}

			{isMobileDevice === true && (
				<ReportListContentMobile
					getReports={getReports}
					dataSource={dataSource}
					useLoading={[loading, setLoading]}
				/>
			)}

			<BackTop target={(): any => document.querySelector('main')} />
		</Container>
	);
}

const Container = styled.main`
	display: flex;
	flex-direction: column;
	position: relative;
`;
